import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatDistanceToNow } from 'date-fns';
import { useClickOutside } from '../../../hooks/useClickOutside';

const PortfolioItemCard = ({ 
  item, 
  onEdit, 
  onDelete, 
  onToggleVisibility, 
  onToggleFeatured,
  disabled = false
}) => {
  const [showActions, setShowActions] = useState(false);
  const dropdownRef = useClickOutside(() => setShowActions(false), showActions);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'website': return 'Globe';
      case 'document': return 'FileText';
      case 'design': return 'Palette';
      case 'social': return 'Share2';
      case 'video': return 'Play';
      default: return 'Briefcase';
    }
  };

  const getStatusColor = (isPublic) => {
    return isPublic ? 'text-success' : 'text-warning';
  };
  
  // Helper function to handle both snake_case and camelCase fields
  const getFieldValue = (fieldName, camelCaseName) => {
    // First check snake_case version, then camelCase version
    const value = item[fieldName] !== undefined ? item[fieldName] : item[camelCaseName];
    // Handle boolean values that might be stored as strings
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  };
  
  // Helper function to specifically handle URL field naming inconsistencies
  const getUrlValue = (dbField, frontendField) => {
    // Check all possible combinations of field names
    const url = item[dbField] || item[frontendField] || item.project_url || item.liveUrl || item.github_url || item.sourceUrl || null;
    // Ensure we don't return [object Object] or other invalid URL strings
    if (url && (typeof url === 'object' || url === '[object Object]')) {
      return null;
    }
    return url;
  };
  
  // Helper function to handle image fields
  const getImageValue = () => {
    const image = item.thumbnail || item.image_url || null;
    // Ensure we don't return [object Object] or other invalid image URLs
    if (image && (typeof image === 'object' || image === '[object Object]')) {
      return null;
    }
    return image;
  };
  
  // Format dates with relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden hover:shadow-lg hover:shadow-primary/10 transition-all duration-200 group">
      {/* Image/Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-secondary-50 to-secondary-100">
        {getImageValue() ? (
          <Image
            src={getImageValue()}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="p-4 bg-white/80 backdrop-blur-sm rounded-full">
              <Icon name={getTypeIcon(item.type)} size={32} className="text-primary" />
            </div>
          </div>
        )}
        
        {/* Status Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <div className={`px-2 py-1 rounded-full text-xs font-medium bg-white ${getStatusColor(getFieldValue('is_public', 'isPublic'))}`}>
            {getFieldValue('is_public', 'isPublic') ? 'Public' : 'Private'}
          </div>
          {(getFieldValue('is_featured', 'isFeatured')) && (
            <div className="px-2 py-1 rounded-full text-xs font-medium bg-warning text-white">
              Featured
            </div>
          )}
        </div>

        {/* Actions Menu */}
        <div className="absolute top-3 right-3">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow"
              disabled={disabled}
            >
              <Icon name="MoreVertical" size={16} className="text-text-secondary" />
            </button>

            {showActions && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border z-10">
                <div className="py-1">
                  <button
                    onClick={() => {
                      onEdit();
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 flex items-center gap-2"
                    disabled={disabled}
                  >
                    <Icon name="Edit" size={14} />
                    Edit Item
                  </button>
                  
                  <button
                    onClick={() => {
                      onToggleVisibility(!getFieldValue('is_public', 'isPublic'));
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 flex items-center gap-2"
                    disabled={disabled}
                  >
                    <Icon name={getFieldValue('is_public', 'isPublic') ? "EyeOff" : "Eye"} size={14} />
                    {getFieldValue('is_public', 'isPublic') ? 'Make Private' : 'Make Public'}
                  </button>
                  
                  <button
                    onClick={() => {
                      onToggleFeatured(!getFieldValue('is_featured', 'isFeatured'));
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 flex items-center gap-2"
                    disabled={disabled}
                  >
                    <Icon name={getFieldValue('is_featured', 'isFeatured') ? "Star" : "Star"} size={14} className={getFieldValue('is_featured', 'isFeatured') ? "fill-current text-warning" : ""} />
                    {getFieldValue('is_featured', 'isFeatured') ? 'Unfeature' : 'Feature'} Item
                  </button>
                  
                  <hr className="my-1" />
                  
                  <button
                    onClick={() => {
                      onDelete();
                      setShowActions(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error-50 flex items-center gap-2"
                    disabled={disabled}
                  >
                    <Icon name="Trash2" size={14} />
                    Delete Item
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 py-3">
        <div className="p-4">
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-text-primary mb-1 line-clamp-2">
              {item.title}
            </h3>
            <div className="text-sm text-text-secondary flex items-center gap-1">
              <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full text-xs font-medium">
                {item.category}
              </span>
              <span className="text-text-tertiary">•</span>
              <span className="text-xs text-text-tertiary">
                {getRelativeTime(item.created_at || item.createdAt)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
            {item.type && (
              <span className="text-text-secondary">{item.type}</span>
            )}
            {item.category && (
              <>
                <span className="text-text-muted">•</span>
                <span className="text-text-secondary">{item.category}</span>
              </>
            )}
            {/* Client display */}
            {getFieldValue('client', 'client') && (
              <>
                <span className="text-text-muted">•</span>
                <span className="text-text-secondary">{getFieldValue('client', 'client')}</span>
              </>
            )}
            {/* Completion date display */}
            {(getFieldValue('completed_date', 'completedDate') || getFieldValue('completedDate', 'completed_date')) && (
              <>
                <span className="text-text-muted">•</span>
                <span className="text-text-secondary">{formatDate(getFieldValue('completed_date', 'completedDate') || getFieldValue('completedDate', 'completed_date'))}</span>
              </>
            )}
            {/* Duration display */}
            {getFieldValue('duration', 'duration') && (
              <>
                <span className="text-text-muted">•</span>
                <span className="text-text-secondary">{getFieldValue('duration', 'duration')}</span>
              </>
            )}
          </div>

          {/* Client Rating */}
          {getFieldValue('client_rating', 'clientRating') && (
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Icon 
                    key={i}
                    name="Star" 
                    size={14} 
                    className={i < getFieldValue('client_rating', 'clientRating') 
                      ? "text-warning fill-current" 
                      : "text-gray-300"} 
                  />
                ))}
                <span className="text-text-secondary ml-1">{getFieldValue('client_rating', 'clientRating')}/5</span>
              </div>
            </div>
          )}
        </div>

        {/* Technologies */}
        {item.technologies && Array.isArray(item.technologies) && item.technologies.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-text-muted mb-1">Technologies:</div>
            <div className="flex flex-wrap gap-1">
              {item.technologies.slice(0, 3).map((tech, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded"
                >
                  {tech}
                </span>
              ))}
              {item.technologies.length > 3 && (
                <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded">
                  +{item.technologies.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Results Preview */}
        {getFieldValue('results', 'results') && Array.isArray(getFieldValue('results', 'results')) && getFieldValue('results', 'results').length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-text-muted mb-1">Key Results:</div>
            <div className="text-sm text-text-secondary">
              {typeof getFieldValue('results', 'results')[0] === 'object' 
                ? JSON.stringify(getFieldValue('results', 'results')[0]) 
                : getFieldValue('results', 'results')[0]}
              {getFieldValue('results', 'results').length > 1 && (
                <span className="text-text-muted"> (+{getFieldValue('results', 'results').length - 1} more)</span>
              )}
            </div>
          </div>
        )}
        
        {/* Full Description Preview - if available */}
        {getFieldValue('full_description', 'fullDescription') && (
          <div className="mb-4">
            <div className="text-xs text-text-muted mb-1">Details:</div>
            <div className="text-sm text-text-secondary line-clamp-2">
              {getFieldValue('full_description', 'fullDescription')}
            </div>
          </div>
        )}

        {/* Links */}
        <div className="flex gap-2">
          {/* Live URL - check all possible field names */}
          {getUrlValue('project_url', 'liveUrl') && (
            <a
              href={getUrlValue('project_url', 'liveUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:text-primary-600 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="ExternalLink" size={14} />
              <span>Live</span>
            </a>
          )}
          {/* Source URL - check all possible field names */}
          {getUrlValue('github_url', 'sourceUrl') && (
            <a
              href={getUrlValue('github_url', 'sourceUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:text-primary-600 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="Github" size={14} />
              <span>Source</span>
            </a>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-3 bg-secondary-50 border-t border-border flex justify-between items-center">
        <div className="text-xs text-text-muted">
          {getFieldValue('images', 'images') && Array.isArray(getFieldValue('images', 'images')) && getFieldValue('images', 'images').length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Icon name="Image" size={12} />
              {getFieldValue('images', 'images').length} image{getFieldValue('images', 'images').length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="Edit"
            onClick={onEdit}
            className="!p-2"
            disabled={disabled}
          />
          <Button
            variant="ghost"
            size="sm"
            iconName={getFieldValue('is_public', 'isPublic') ? "Eye" : "EyeOff"}
            onClick={() => onToggleVisibility(!getFieldValue('is_public', 'isPublic'))}
            className={`!p-2 ${getFieldValue('is_public', 'isPublic') ? 'text-success' : 'text-warning'}`}
            disabled={disabled}
          />
        </div>
      </div>

    </div>
  );
};

export default PortfolioItemCard;
